/**
 *
 * MusicStyleInput
 *
 */
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components/macro';
import { ToggleButtonGroup, ToggleButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { faChevronDown, faChevronUp, faChevronRight, faDice, faSearch} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TextInputBox } from '../TextInputBox';
import { AudioSelectButtonModal } from '../AudioSelectButtonModal/Loadable';
import { FileWithPreview } from '../AudioFileUploader';
import { BrowseSongReferencesModal } from '../BrowseSongReferencesModal/Loadable';


const MUSIC_STYLES = [
  'Rock',
  'Jazz',
  'Hiphop',
  'Pop',
  'Classical',
  'Electronic',
  'Country',
  'R&B',
  'Funk',
];

// Example related words mapping
const RELATED_WORDS: Record<string, string[]> = {
  // Genre -> Mood
  Rock: ['Energetic', 'Uplifting', 'Dark', 'Rebellious'],
  Jazz: ['Chill', 'Sophisticated', 'Melancholic', 'Groovy'],
  Hiphop: ['Energetic', 'Confident', 'Aggressive', 'Playful'],
  Pop: ['Happy', 'Uplifting', 'Romantic', 'Dancey'],
  Classical: ['Majestic', 'Melancholic', 'Peaceful', 'Dramatic'],
  Electronic: ['Energetic', 'Mysterious', 'Uplifting', 'Hypnotic'],
  Country: ['Nostalgic', 'Heartfelt', 'Upbeat', 'Melancholic'],
  'R&B': ['Smooth', 'Romantic', 'Soulful', 'Chill'],
  Funk: ['Groovy', 'Upbeat', 'Playful', 'Energetic'],

  // Mood -> Instruments
  Energetic: ['Electric Guitar', 'Drums', 'Synth', 'Bass'],
  Uplifting: ['Acoustic Guitar', 'Piano', 'Strings', 'Brass'],
  Dark: ['Synth', 'Bass', 'Electric Guitar', 'Drums'],
  Rebellious: ['Electric Guitar', 'Drums'],
  Chill: ['Piano', 'Saxophone', 'Electric Piano', 'Soft Drums'],
  Sophisticated: ['Saxophone', 'Double Bass', 'Piano'],
  Melancholic: ['Violin', 'Piano', 'Cello', 'Acoustic Guitar'],
  Groovy: ['Bass', 'Electric Guitar', 'Drums'],
  Confident: ['808', 'Synth', 'Rap Vocals'],
  Aggressive: ['Drums', 'Electric Guitar', 'Synth'],
  Playful: ['Synth', 'Brass', 'Percussion'],
  Happy: ['Ukulele', 'Piano', 'Claps'],
  Romantic: ['Strings', 'Piano', 'Acoustic Guitar'],
  Dancey: ['Synth', 'Drums', 'Bass'],
  Majestic: ['Brass', 'Strings', 'Timpani'],
  Peaceful: ['Flute', 'Acoustic Guitar', 'Piano'],
  Dramatic: ['Strings', 'Brass', 'Percussion'],
  Mysterious: ['Synth', 'Flute', 'Bass'],
  Nostalgic: ['Acoustic Guitar', 'Harmonica', 'Fiddle'],
  Heartfelt: ['Acoustic Guitar', 'Piano', 'Pedal Steel'],
  Upbeat: ['Banjo', 'Fiddle', 'Drums'],
  Smooth: ['Electric Piano', 'Saxophone', 'Bass'],
  Soulful: ['Organ', 'Electric Guitar', 'Vocals'],
};

interface Props {
  onChangeText?: (text: string) => void;
  onAudioFilesChange: (files: FileWithPreview[] | ((prev: FileWithPreview[]) => FileWithPreview[])) => void;
  audioFiles: FileWithPreview[];
  onLoadedMetadata: (e: any, audioKey: any, durationOverride?: any) => void;
  audioKeyOffset: number;
  hideLinkInput?: boolean;
  totalUploadProgress: number;
  error?: string;
  onAudioVocalStyleFilesChange?: (files: FileWithPreview[] | ((prev: FileWithPreview[]) => FileWithPreview[])) => void;
  audioVocalStyleFiles?: FileWithPreview[];
  onLoadedMetadataVocalStyle?: (e: any, audioKey: any, durationOverride?: any) => void;
  audioKeyOffsetVocalStyle?: number;
  totalUploadProgressVocalStyle?: number;
  errorVocalStyle?: string;
  showVocalStyle?: boolean;
}

// Styled components
const Container = styled.div`
  background: #180D23;
  border-radius: 15px;
  margin-top: 12px;
  padding: 15px;
  width: 100%;
  color: #F5E1FF;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  @media (max-width: 768px) {
    width: 100%;
    justify-content:center;
  }
`;

const ArrowButton = styled.button`
  background: none;
  border: none;
  color: #F5E1FF;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0 8px 0 0;
`;

const Title = styled.h6`
  font-family: 'Jura', sans-serif;
  font-weight: 700;
  font-size: 16px;
  margin: 0;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Content = styled.div<{collapsed: boolean}>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s;
  max-height: ${({collapsed}) => (collapsed ? '0' : '800px')};
  opacity: ${({collapsed}) => (collapsed ? 0 : 1)};
  pointer-events: ${({collapsed}) => (collapsed ? 'none' : 'auto')};
`;

const ChipsRowWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 10px;
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  overflow-x: auto;
  margin-right: 28px;
  white-space: nowrap;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;  /* IE 10+ */
  &::-webkit-scrollbar {
    display: none;
  }
  width: 100%;
`;

const ScrollArrow = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(24, 13, 35);
  color: #fff;
  border: none;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
`;

const Chip = styled.button<{ selected: boolean }>`
  background: ${({ selected }) => (selected ? 'rgba(255, 116, 233, 0.12)' : 'rgba(245, 225, 255, 0.04)')};
  color: var(--primary-light, rgba(245, 225, 255, 0.74));
  border: none;
  border-radius: 42px;
  height: 24px;
  padding: 4px 12px;
  font-family: 'Raleway', sans-serif;
  font-size: 13px;
  font-weight: 400;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const Description = styled.div`
  color: var(--text-disabled, rgba(245, 225, 255, 0.74));
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: Raleway;
  font-size: 0.8rem;
  font-style: normal;
  font-weight: 400;
  letter-spacing: 0.00938rem;
`;

const VocalStyleTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const UploadAudioOptionsRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  div {
    flex: 1;
  }  
`;

const BrowseStylesButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: #f5e1ff;
  font-size: 12px;
  font-family: 'Jura', sans-serif;
  font-weight: 500;
  text-transform: uppercase;
  flex: 1;
  border: 1px solid #645270;
  border-radius: 20px;
  padding: 24px 16px;
  cursor: pointer;
  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }
`;

export function MusicStyleInput(props: Props) {
  const [collapsed, setCollapsed] = useState(false);
  const [toggle, setToggle] = useState<'AUDIO_REF' | 'TEXT'>('AUDIO_REF');
  const [input, setInput] = useState('');
  const [isBrowseSongReferencesModalOpen, setIsBrowseSongReferencesModalOpen] = useState(false);
  const chipsRowRef = useRef<HTMLDivElement>(null);
  const [showRightArrow, setShowRightArrow] = useState(false);
  // For drill-down: track current chip list and parent
  const [currentChipList, setCurrentChipList] = useState<string[]>(MUSIC_STYLES);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const showVocalStyle = props.showVocalStyle ?? true;
  useEffect(() => {
    const checkOverflow = () => {
      const el = chipsRowRef.current;
      if (el) {
        setShowRightArrow(el.scrollWidth > el.clientWidth);
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [currentChipList]);

  const handleScrollRight = () => {
    const el = chipsRowRef.current;
    if (el) {
      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      // If at (or near) the end, scroll to start
      if (el.scrollLeft + 5 >= maxScrollLeft) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: 100, behavior: 'smooth' });
      }
    }
  };

  const isStyleInInput = (style: string) => {
    return input
      .split(',')
      .map(s => s.trim().toLowerCase())
      .filter(Boolean)
      .includes(style.toLowerCase());
  };

  // Drill-down chip click handler
  const handleDrillChipClick = (chip: string) => {
    // Update input as before
    setInput(prevInput => {
      let items = prevInput.split(',').map(s => s.trim()).filter(Boolean);
      const lowerItems = items.map(s => s.toLowerCase());
      if (lowerItems.includes(chip.toLowerCase())) {
        // Remove the chip (case-insensitive)
        items = items.filter(s => s.toLowerCase() !== chip.toLowerCase());
        return items.join(items.length ? ', ' : '');
      } else {
        // Add the chip if not present
        return items.length > 0 ? items.join(', ') + ', ' + chip : chip;
      }
    });
    // Drill down if there are related words
    const related = RELATED_WORDS[chip];
    if (related && related.length > 0) {
      setCurrentChipList(related);
    }
    // If no related, do not change chip list
  };

  const handleRandomize = () => {
    // Pick a random top-level style
    const top = MUSIC_STYLES[Math.floor(Math.random() * MUSIC_STYLES.length)];
    const related = RELATED_WORDS[top] || [];
    // Pick a random subset of related (1-3, or all if less)
    let subset: string[] = [];
    if (related.length > 0) {
      const count = Math.min(related.length, Math.floor(Math.random() * 3) + 1); // 1-3
      // Shuffle and pick count
      subset = [...related].sort(() => 0.5 - Math.random()).slice(0, count);
    }
    setInput([top, ...subset].join(', '));
  };

  // Reset to top level if input is cleared
  useEffect(() => {
    if (input.trim() === '') {
      setCurrentChipList(MUSIC_STYLES);
    }
  }, [input]);
  return (
    <Container>
      <Header>
        <Title>
          DETERMINE MUSIC STYLE
        </Title>
        <HeaderRight>
          <ToggleButtonGroup
            exclusive
            value={toggle}
            onChange={(_, value) => {
              if (value) {
                setToggle(value);
                setCollapsed(false); // Automatically expand when switching modes
              }
            }}
            color="primary"
            sx={{
              '--ButtonGroup-radius': '15px',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '25px',
              overflow: 'hidden',
              height: '30px',
              width: '100%',
              '& .MuiToggleButton-root': {
                fontFamily: 'Raleway',
                fontSize: '11px',
                fontWeight: 400,
                borderRadius: '0px !important',
                height: '100%',
                minHeight: '100%',
                padding: '0 12px',
                color: '#F5E1FF',
                flex: 1,
                whiteSpace: 'nowrap',
                '&.Mui-selected': {
                  color: '#FF74E9',
                  background: 'rgba(255, 116, 233, 0.12)',
                },
              },
              '& .MuiToggleButtonGroup-grouped': {
                border: 'none',
                borderRadius: '0px !important',
              },
              '@media (max-width: 768px)': {
                '& .MuiToggleButton-root': {
                  fontSize: '13px',
                },
              },
            }}
            size="small"
          >
            <ToggleButton value="AUDIO_REF">
              AUDIO REF
            </ToggleButton>
            <ToggleButton value="TEXT">
              TEXT
            </ToggleButton>
          </ToggleButtonGroup>
        </HeaderRight>
      </Header>

        {toggle === 'TEXT' && (
          <ChipsRowWrapper>
            <ChipsRow ref={chipsRowRef}>
              {currentChipList.map(style => (
                <Chip
                  key={style}
                  selected={isStyleInInput(style)}
                  onClick={() => handleDrillChipClick(style)}
                >
                  {style}
                </Chip>
              ))}
            </ChipsRow>
            {showRightArrow && (
              <ScrollArrow onClick={handleScrollRight}>
                <FontAwesomeIcon icon={faChevronRight} style={{color: 'rgba(245, 225, 255, 0.74)'}} />
              </ScrollArrow>
            )}
          </ChipsRowWrapper>
        )}
        {toggle === 'TEXT' ? (
          <TextInputBox
            value={input}
            onChange={val => {
              setInput(val);
              if (props.onChangeText) props.onChangeText(val);
            }}
            placeholder="Peaceful Jazz etc."
            maxLength={300}
            charCount={true}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
            buttons={[{
              label: 'Randomize',
              onClick: handleRandomize,
              variant: 'secondary',
              icon: <FontAwesomeIcon icon={faDice} style={{color: 'rgba(245, 225, 255, 0.74)'}} />,
            }]}
            focused={isInputFocused}
          />
        ) : (
          <div style={{marginTop: '5px'}}>
            <Description>
              Shapes the overall sound, melody, and mood of the song.
            </Description>
            {/* <BrowseUploadButtons /> */}
              <UploadAudioOptionsRow>
                {props.audioFiles.length === 0 && (
                  <BrowseStylesButton onClick={() => setIsBrowseSongReferencesModalOpen(true)}>
                    <FontAwesomeIcon icon={faSearch} style={{color: '#FF74E9'}} size='lg' />
                    Browse Styles
                  </BrowseStylesButton>
                )}
                <AudioSelectButtonModal
                  minimalVersion
                  renderInPlace
                  title="Select Reference Track"
                  hideDescription={true}
                  buttonStyles={{
                    marginBottom: '20px',
                    padding: '20px',
                    fontSize: isMobile ? '16px' : '20px',
                  }}
                  small={true}
                  onAudioFilesChange={props.onAudioFilesChange}
                  audioFiles={props.audioFiles}
                  onLoadedMetadata={props.onLoadedMetadata}
                  audioKeyOffset={props.audioKeyOffset}
                  maxFiles={1}
                  hideLinkInput={props.hideLinkInput}
                  totalUploadProgress={props.totalUploadProgress}
                  error={props.error}
                  hideRecordButton
                />
              </UploadAudioOptionsRow>
              {showVocalStyle && (
                <>
                  <VocalStyleTitleContainer>
                    <div>
                      <HeaderLeft>
                        <ArrowButton onClick={() => setCollapsed(c => !c)}>
                          <FontAwesomeIcon icon={collapsed ? faChevronDown : faChevronUp} style={{color: 'rgba(245, 225, 255)', marginTop: '2px'}} />
                        </ArrowButton>
                        <Title>ADD VOCAL STYLE</Title>
                      </HeaderLeft>
                      <Description>
                        Influences the vocal timbre, melody, and rhythm of the final song.
                      </Description>
                    </div>
                  </VocalStyleTitleContainer>
                  <Content collapsed={collapsed}>
                    <UploadAudioOptionsRow>
                      {(props.audioVocalStyleFiles?.length ?? 0) === 0 && (
                        <BrowseStylesButton onClick={() => setIsBrowseSongReferencesModalOpen(true)}>
                          <FontAwesomeIcon icon={faSearch} style={{color: '#FF74E9'}} size='lg' />
                          Browse Styles
                        </BrowseStylesButton>
                      )}
                      <AudioSelectButtonModal
                        minimalVersion
                        renderInPlace
                        title="Select Reference Track"
                        hideDescription={true}
                        buttonStyles={{
                          marginBottom: '20px',
                          padding: '20px',
                          fontSize: isMobile ? '16px' : '20px',
                        }}
                        small={true}
                        onAudioFilesChange={props.onAudioVocalStyleFilesChange ?? (() => {})}
                        audioFiles={props.audioVocalStyleFiles ?? []}
                        onLoadedMetadata={props.onLoadedMetadataVocalStyle ?? (() => {})}
                        audioKeyOffset={props.audioKeyOffsetVocalStyle ?? 0}
                        maxFiles={1}
                        hideLinkInput={props.hideLinkInput}
                        totalUploadProgress={props.totalUploadProgressVocalStyle ?? 0}
                        error={props.errorVocalStyle}
                        hideRecordButton
                      />
                    </UploadAudioOptionsRow>
                  </Content>
                </>
              )}
          </div>
        )}
      <BrowseSongReferencesModal open={isBrowseSongReferencesModalOpen} onClose={() => setIsBrowseSongReferencesModalOpen(false)} />
    </Container>
  );
}
